'use client'

// RegionContent abstraction (Overmind→server-props migration, Phase A2.1).
//
// WHAT THIS IS
// The dedicated seam that supplies the per-page CMS content collections —
// the full FAQ list (`faqs`) and the blog-index list (`blogPosts`) — to the
// section components that render them, sourced from the SERVER-FETCHED page
// data threaded as props, NOT from a render-time Overmind `state.faqs` /
// `state.blogPosts` read. It mirrors components/NamedPrices exactly: a
// `'use client'` context Provider + two accessor hooks (`useRegionFaqs()`,
// `useRegionBlogPosts()`) so the ~4 section leaves that consumed those store
// fields (sections/Products/ProductFaq, sections/FaqPage,
// sections/FaqCollapsibleList, sections/Blog/BlogPage) no longer import
// Overmind at all — they import this module.
//
// DATA SOURCE
// lib/regionPageData.loadRegionPageData already fetches these collections
// (FAQS_QUERY -> `faqs`, BLOG_POSTS_QUERY -> `blogPosts`) for any page that
// renders a FAQ / blog-index section and returns them. They are threaded into
// <RegionContentProvider faqs={...} blogPosts={...}> in the App Router page
// island (RegionPageClient), so the values are available DURING the server SSR
// render and during the client's first render — the SAME serialized arrays that
// appRendered({ faqs, blogPosts }) still hydrates into state.faqs/state.blogPosts
// (see FALLBACK) — which is what keeps the FAQ/blog render hydration-safe (SSR
// HTML === first client render, no React #418/#423). ZERO new fetch: this is a
// pure threading of already-loaded data.
//
// APP-ROUTER SAFETY
// This module is a `'use client'` boundary (it holds React context). It is
// imported ONLY by client components (the FAQ/blog section leaves) — so it
// introduces no new server-component/RSC coupling. No next/router, no window at
// render.
//
// SERVER-PROPS ONLY (Overmind retirement, Phase D2)
// The transitional Overmind fallback (state.faqs / state.blogPosts) is GONE: the
// hooks return purely the server-threaded collections. In production every FAQ/blog
// section renders under the <RegionContentProvider> RegionPageClient establishes,
// fed the collections loadRegionPageData already fetches. An absent/empty provider
// value yields an empty array (never throws).

import React, { createContext, useContext } from 'react'

// Server-threaded { faqs, blogPosts }, or null when no provider is present.
const RegionContentContext = createContext(null)

export function RegionContentProvider({ faqs = null, blogPosts = null, children }) {
  // A stable value object per render is fine here: the provider sits at the page
  // island root (RegionPageClient), which re-renders rarely, and the consumers
  // read plain arrays (no referential-identity effects depend on it).
  return (
    <RegionContentContext.Provider value={{ faqs, blogPosts }}>
      {children}
    </RegionContentContext.Provider>
  )
}

/**
 * The full CMS FAQ collection (`*[_type == "faq"]`) for the FAQ sections. Returns
 * the server-threaded array (empty when absent) so callers can spread/map/filter
 * without a guard.
 * @returns {any[]}
 */
export function useRegionFaqs() {
  const ctx = useContext(RegionContentContext)
  const serverFaqs = ctx ? ctx.faqs : null
  return Array.isArray(serverFaqs) ? serverFaqs : []
}

/**
 * The full blog-index list (`*[_type == "blogPost"]`) for the blog index. Same
 * server-props contract as useRegionFaqs().
 * @returns {any[]}
 */
export function useRegionBlogPosts() {
  const ctx = useContext(RegionContentContext)
  const serverBlogPosts = ctx ? ctx.blogPosts : null
  return Array.isArray(serverBlogPosts) ? serverBlogPosts : []
}

export { RegionContentContext }
